import { isShowableBand, lowestSkills } from '@/domain/results/band-display'
import { studentResultStatus, studentStatusInputFromResult, type SkillKey, type StudentResultStatus } from '@/domain/results/student-status'
import { STUDY_DESTINATION_TIERS, guideStudyDestinations, type StudyDestinationTier } from '@/domain/scoring/study-destinations'
import { publishedSkillBand, type DashboardSkillField } from '@/features/dashboard/focus-skill'
import type { ImprovementPlan, PlanRecommendation } from '@/features/plan/plan-api'
import { t } from '@/lib/i18n'
import type { LeaderboardEntry, LeaderboardResponse, StudentDashboardStats, TestResult } from '@/lib/types'

/**
 * The facts Home shows, worked out from data the page already fetched (PAGES.md 3.4). Pure functions: no fetching, and
 * nothing here invents a number. A band that is missing or 0 comes back as `null`, never as a value to print.
 */

export const SKILL_ORDER: readonly SkillKey[] = ['listening', 'reading', 'writing', 'speaking']

export type DayPeriod = 'morning' | 'afternoon' | 'evening'

/** 05:00 to 11:59 morning, 12:00 to 16:59 afternoon, otherwise evening (device clock). */
export function dayPeriod(date: Date): DayPeriod {
  const hour = date.getHours()
  if (hour >= 5 && hour < 12) return 'morning'
  if (hour >= 12 && hour < 17) return 'afternoon'
  return 'evening'
}

/** The h1. `name` is already isolated for the interface language; with no name the heading is plain "Home". */
export function greeting(period: DayPeriod, name: string | null): string {
  if (!name) return t('Home')
  if (period === 'morning') return t('Good morning, {0}.', { 0: name })
  if (period === 'afternoon') return t('Good afternoon, {0}.', { 0: name })
  return t('Good evening, {0}.', { 0: name })
}

/** A full mock covers all four skills, by `test_types` when the server sends it and by the section scores otherwise. */
export function isFullMock(result: Pick<TestResult, 'test_types' | 'section_scores'>): boolean {
  const named = new Set((result.test_types ?? []).map((type) => type.toLowerCase()))
  if (SKILL_ORDER.every((skill) => named.has(skill))) return true
  const scores = (result.section_scores ?? {}) as unknown as Record<string, unknown>
  return SKILL_ORDER.every((skill) => scores[skill] !== undefined && scores[skill] !== null)
}

const byNewest = (left: TestResult, right: TestResult) => Date.parse(right.completed_at) - Date.parse(left.completed_at)

/** Completed attempts only, newest first. An abandoned attempt is never a "latest" anything. */
export function completedNewestFirst(rows: readonly TestResult[]): TestResult[] {
  return rows.filter((row) => row.status !== 'abandoned').sort(byNewest)
}

export interface LatestMock {
  result: TestResult
  status: StudentResultStatus
  /** The four section bands, showable ones only (`null` otherwise), in Listening, Reading, Writing, Speaking order. */
  bands: Record<SkillKey, number | null>
}

export function sectionBands(result: TestResult): Record<SkillKey, number | null> {
  const scores = (result.section_scores ?? {}) as unknown as Record<string, { band?: number | null } | null | undefined>
  const pick = (skill: SkillKey) => {
    const band = scores[skill]?.band
    return isShowableBand(band) ? band : null
  }
  return { listening: pick('listening'), reading: pick('reading'), writing: pick('writing'), speaking: pick('speaking') }
}

export function latestMock(rows: readonly TestResult[]): LatestMock | null {
  const result = completedNewestFirst(rows).find(isFullMock)
  if (!result) return null
  return { result, status: studentResultStatus(studentStatusInputFromResult(result)), bands: sectionBands(result) }
}

export interface ChartPoint { date: string; band: number; resultId: string }

/** Overall band at each full mock, oldest first, at most eight. Only a showable overall band is a point. */
export function chartPoints(rows: readonly TestResult[], limit = 8): ChartPoint[] {
  const points: ChartPoint[] = []
  for (const row of completedNewestFirst(rows).filter(isFullMock)) {
    const band = studentResultStatus(studentStatusInputFromResult(row)).band
    if (isShowableBand(band) && !Number.isNaN(Date.parse(row.completed_at))) points.push({ date: row.completed_at, band, resultId: row.id })
  }
  return points.slice(0, limit).reverse()
}

/** The gap to the target in bands, or `null` when either side is missing. Negative or zero means on target. */
export function gapToTarget(overall: number | null | undefined, target: number | null | undefined): number | null {
  if (!isShowableBand(overall) || !isShowableBand(target)) return null
  return Math.round((target - overall) * 2) / 2
}

export interface FocusInputSkill { field: DashboardSkillField; band: number | null }

/** Each skill's band for the focus pick: the latest full mock's band when it has one, else the lifetime/latest stats band. */
export function focusSkills(mock: LatestMock | null, stats: Pick<StudentDashboardStats, 'averageSectionBands' | 'latestSectionBands'> | null | undefined): FocusInputSkill[] {
  return SKILL_ORDER.map((field) => ({ field, band: mock?.bands[field] ?? publishedSkillBand(field, stats) }))
}

export interface LowestSkill { skill: SkillKey; band: number }

/** The lowest skill of the latest full mock (yellow, never red), or `null` when fewer than two bands or all equal. */
export function lowestOf(bands: Record<SkillKey, number | null>): LowestSkill | null {
  const entries = SKILL_ORDER.map((skill) => ({ skill, band: bands[skill] }))
  const lowest = lowestSkills(entries)
  const first = SKILL_ORDER.find((skill) => lowest.has(skill))
  const band = first ? bands[first] : null
  return first && isShowableBand(band) ? { skill: first, band } : null
}

export interface UnlockModel {
  /** The tier the overall band sits in. */
  current: StudyDestinationTier
  /** The next tier up, or `null` at the top. */
  next: StudyDestinationTier | null
}

/** "What your band unlocks": the current tier and the next one up (PAGES.md 3.4). `null` without a reportable overall band. */
export function unlockModel(overall: number | null | undefined): UnlockModel | null {
  // 0.0 is a reportable IELTS band to the guidance, but never one a student sees as theirs (DECISIONS 12).
  if (!isShowableBand(overall)) return null
  const guidance = guideStudyDestinations(overall)
  if (guidance.status !== 'guided') return null
  const index = STUDY_DESTINATION_TIERS.findIndex((tier) => tier.id === guidance.tier.id)
  return { current: guidance.tier, next: STUDY_DESTINATION_TIERS[index + 1] ?? null }
}

export interface BoardRow { entry: LeaderboardEntry; own: boolean }
export interface BoardModel {
  rows: BoardRow[]
  /** True when the signed-in student is not in the top rows and is appended after a divider. */
  ownAppended: boolean
  rank: number | null
  of: number
}

/** The top five plus the student's own row (appended after a divider when they are not among the five). */
export function boardModel(data: LeaderboardResponse, top = 5): BoardModel {
  const own = data.current_user ?? null
  const rows: BoardRow[] = (data.leaderboard ?? []).slice(0, top).map((entry) => ({ entry, own: own !== null && entry.user_id === own.user_id }))
  const present = rows.some((row) => row.own)
  const ownAppended = own !== null && !present
  if (ownAppended && own) rows.push({ entry: own, own: true })
  return { rows, ownAppended, rank: data.current_user_rank ?? null, of: data.stats?.active_test_takers ?? 0 }
}

/**
 * "This week": the planner's recommendations, first three, the ones still short of the target before any "maintain" one.
 * There is no counsellor task data (PAGES.md 3.4), so these are plan items and the card says "From your plan".
 */
export function weekItems(plan: Pick<ImprovementPlan, 'recommendations'> | null | undefined, limit = 3): PlanRecommendation[] {
  const items = plan?.recommendations ?? []
  const short = (item: PlanRecommendation) => item.current_band === null || item.current_band < item.target_band
  return [...items.filter(short), ...items.filter((item) => !short(item))].slice(0, limit)
}
